import { useEffect, useState } from 'react';
import { apiGet, apiSend } from '@/lib/api';
import { PageHeader, Card, Spinner, EmptyState, ErrorNote } from '@/components/ui';

interface LogEntry {
  id: string;
  notification_type: string;
  status: string;
  sent_at: string;
  pilot_name?: string;
  departure_airport?: string;
  arrival_airport?: string;
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent: string }) {
  return (
    <Card className="p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-2 text-3xl font-bold tabular-nums ${accent}`}>{value}</p>
    </Card>
  );
}

export default function Notifications() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [unlogged, setUnlogged] = useState(0);
  const [escalated, setEscalated] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  function load() {
    Promise.all([
      apiGet<{ logs: LogEntry[] }>('/api/harassment/log'),
      apiGet<{ unlogged_count: number }>('/api/harassment/unlogged'),
      apiGet<{ escalated_count: number }>('/api/harassment/escalated'),
    ])
      .then(([l, u, e]) => {
        setLogs(l.logs || []);
        setUnlogged(u.unlogged_count || 0);
        setEscalated(e.escalated_count || 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function runCheck() {
    setChecking(true);
    try { await apiSend('/api/harassment/check', 'POST'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Check failed'); }
    finally { setChecking(false); }
  }

  const pending = logs.filter((l) => l.status === 'pending').length;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Notifications"
        subtitle="Reminders to log detected flights."
        actions={<button onClick={runCheck} disabled={checking} className="btn-ghost text-sm">{checking ? 'Checking…' : 'Run check'}</button>}
      />

      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      {loading ? (
        <Spinner />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Unlogged" value={unlogged} accent="text-ember" />
            <Stat label="Pending" value={pending} accent="text-amber-300" />
            <Stat label="Escalated" value={escalated} accent="text-red-300" />
          </div>

          <Card className="p-5 sm:p-6">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">History</h2>
            {logs.length === 0 ? (
              <EmptyState
                title="No notifications"
                hint="When aircraft tracking is enabled and a flight goes unlogged, reminders appear here."
              />
            ) : (
              <ul className="divide-y divide-white/5">
                {logs.map((l) => (
                  <li key={l.id} className="-mx-2 flex items-center justify-between rounded-lg px-2 py-3 text-sm transition hover:bg-white/[0.03]">
                    <div>
                      <p className="text-slate-200">
                        {l.departure_airport && l.arrival_airport
                          ? `${l.departure_airport} → ${l.arrival_airport}`
                          : l.notification_type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-500">{new Date(l.sent_at).toLocaleString()}{l.pilot_name ? ` · ${l.pilot_name}` : ''}</p>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${
                      l.status === 'logged' ? 'bg-emerald-500/15 text-emerald-300'
                        : l.status === 'acknowledged' ? 'bg-sky-500/15 text-sky-300'
                        : 'bg-amber-500/15 text-amber-300'
                    }`}>{l.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
